import { useState, useRef, useCallback, useEffect } from 'react';
import {
  pollJob,
  simulateImageToVideoJob,
} from '../services/videoGeneration';
import { enhancePrompt } from '../services/promptEnhancer';
import { getOrCreateAnonId } from '../utils/anonId';
import { apiClient } from '../services/api';
import { POLLING } from '../utils/constants';
import { GenerationStage } from '../types/progression';
import { invalidateUserAssetsCache, getAsset } from '../services/assets';
import { API_URL } from '../config/apiConfig';
import { getVideoUrl } from '../utils/videoUtils';
import type { User } from '../types/api';
import { useTracking } from './useTracking';

export type GenerationStatus = 'idle' | 'generating' | 'completed' | 'failed';

export interface UseVideoGenerationOptions {
  assetId: string;
  prompt: string;
  userEmail?: string;
  userData?: User | null; // User data to determine watermarked vs unwatermarked URL
  onProgress?: (progress: number) => void;
  onStatusChange?: (status: GenerationStatus) => void;
  onComplete?: (videoUrl: string) => void;
  onError?: (error: string | { type: string; message?: string; current_credits?: number; required_credits?: number }) => void;
  // Progression callbacks for detailed stage tracking
  onStageChange?: (stage: GenerationStage, message?: string) => void;
  onStageProgress?: (stage: GenerationStage, progress: number) => void;
}

export interface UseVideoGenerationReturn {
  status: GenerationStatus;
  progress: number;
  videoUrl: string | null;
  error: string | null;
  startGeneration: () => Promise<void>;
  cancelGeneration: () => void;
  reset: () => void;
}

/**
 * Reusable hook for video generation with progress tracking
 * Extracted from ImageToVideoPage for use across multiple components
 */
export function useVideoGeneration({
  assetId,
  prompt,
  userEmail,
  userData,
  onProgress,
  onStatusChange,
  onComplete,
  onError,
  onStageChange,
  onStageProgress,
}: UseVideoGenerationOptions): UseVideoGenerationReturn {
  const { track } = useTracking();
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const cancelSimulationRef = useRef<(() => void) | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const videoGenProgressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingPausedRef = useRef<boolean>(false);
  const lastVisibilityChangeRef = useRef<number>(Date.now());
  const jobIdRef = useRef<string | null>(null);
  const assetIdRef = useRef<string | null>(null);

  // Notify status changes
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(status);
    }
  }, [status, onStatusChange]);

  // Notify progress changes
  useEffect(() => {
    if (onProgress) {
      onProgress(progress);
    }
  }, [progress, onProgress]);

  // Notify completion
  useEffect(() => {
    if (status === 'completed' && videoUrl && onComplete) {
      onComplete(videoUrl);
    }
  }, [status, videoUrl, onComplete]);

  // Notify errors
  useEffect(() => {
    if (status === 'failed' && error && onError) {
      onError(error);
    }
  }, [status, error, onError]);

  /**
   * Helper function to determine the correct video URL based on user's subscription
   * Fetches the asset and uses getVideoUrl utility to determine watermarked vs unwatermarked URL
   */
  const determineVideoUrl = useCallback(async (url: string, currentAssetId: string): Promise<string> => {
    try {
      // Fetch the asset to get both video_url and watermarked_video_url
      const asset = await getAsset(currentAssetId);
      
      if (!asset) {
        console.warn('⚠️ [useVideoGeneration] Could not fetch asset, using default URL');
        return url;
      }
      
      // Use getVideoUrl utility to determine correct URL based on user's subscription
      const appropriateUrl = getVideoUrl(asset, userData || null);
      
      return appropriateUrl || url; // Fallback to original URL if utility returns undefined
    } catch (error) {
      console.error('❌ [useVideoGeneration] Error determining video URL:', error);
      return url; // Fallback to original URL on error
    }
  }, [userData]);

  const startGeneration = useCallback(async () => {
    if (!assetId || !assetId.trim() || !prompt.trim()) {
      setError('Asset ID and prompt are required');
      setStatus('failed');
      return;
    }
    
    // CRITICAL: Validate asset_id is a valid UUID format (Supabase asset IDs are UUIDs)
    // Generation will fail with Supabase errors if asset_id is not a UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(assetId)) {
      const errorMsg = `Asset ID does not match UUID format: ${assetId}. Generation cannot proceed without a valid UUID asset_id from Supabase.`;
      console.error('❌ [useVideoGeneration]', errorMsg);
      setError(errorMsg);
      setStatus('failed');
      if (onError) {
        onError(errorMsg);
      }
      return; // Stop generation - do not proceed with invalid asset_id
    }

    setStatus('generating');
    setProgress(0);
    setError(null);
    setVideoUrl(null);
    startTimeRef.current = Date.now();

    // Cancel any existing simulation
    if (cancelSimulationRef.current) {
      cancelSimulationRef.current();
    }

    // Clear any existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    if (!API_URL) {
      // Mock mode - simulate job
      const cancel = simulateImageToVideoJob(
        (prog) => setProgress(prog),
        async (_jobId, url) => {
          // Determine the correct video URL based on user's subscription
          const appropriateUrl = await determineVideoUrl(url, assetId);
          setVideoUrl(appropriateUrl);
          setProgress(100);
          setStatus('completed');
        }
      );
      cancelSimulationRef.current = cancel;
      return;
    }

    // Online mode - use real API
    try {
      const anonId = !userEmail ? getOrCreateAnonId() : undefined;

      // Get image URL from sessionStorage or fetch from asset
      let imageUrl: string | undefined;
      const storedImageUrl = sessionStorage.getItem('uploaded_image_url');
      if (storedImageUrl) {
        imageUrl = storedImageUrl;
      } else {
        // Try to fetch asset to get s3_url
        try {
          const { getAsset } = await import('../services/assets');
          const asset = await getAsset(assetId);
          if (asset?.s3_url) {
            imageUrl = asset.s3_url;
          }
        } catch (err) {
          console.warn('⚠️ [useVideoGeneration] Could not fetch asset for image URL:', err);
        }
      }

      // Validate that we have image_url (required by prompt enhancer)
      if (!imageUrl) {
        throw new Error('Image URL is required for video generation');
      }

      const shortPrompt = prompt?.trim().substring(0, 50) || 'No prompt';

      // Notify stage change: enhancing prompt
      onStageChange?.('enhancing_prompt', 'Enhancing prompt with AI...');

      // Simulate progress for enhance prompt stage (15 seconds max)
      const enhanceStartTime = Date.now();
      const ENHANCE_MAX_TIME = 15000; // 15 seconds
      const enhanceProgressInterval = setInterval(() => {
        const elapsed = Date.now() - enhanceStartTime;
        const progress = Math.min(95, (elapsed / ENHANCE_MAX_TIME) * 100); // Cap at 95% until complete
        onStageProgress?.('enhancing_prompt', progress);
      }, 200); // Update every 200ms for smooth progress

      // Step 1: Call prompt enhancer to get enhanced prompts
      // Wrap in try-catch to handle timeouts and network errors gracefully
      let enhancedPrompts: Awaited<ReturnType<typeof enhancePrompt>>;
      try {
        enhancedPrompts = await enhancePrompt({
          image_url: imageUrl,
          prompt: prompt.trim(),
          asset_id: assetId, // Include asset_id for enhance-from-supabase endpoint
        });
        // Clear progress interval and set to 100%
        clearInterval(enhanceProgressInterval);
        onStageProgress?.('enhancing_prompt', 100);
      } catch (error) {
        clearInterval(enhanceProgressInterval);
        console.error('❌ [useVideoGeneration] Prompt enhancement failed after all retries:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to enhance prompt';
        
        // Provide more helpful error messages for mobile users
        let userFriendlyError = errorMessage;
        if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
          userFriendlyError = 'Prompt enhancement timed out. Please check your connection and try again.';
        } else if (errorMessage.includes('Network error') || errorMessage.includes('Failed to connect')) {
          userFriendlyError = 'Network connection failed. Please check your internet connection and try again.';
        }
        
        setError(userFriendlyError);
        setStatus('failed');
        onError?.(userFriendlyError);
        throw error; // Re-throw to stop execution
      }
      
      // Validate enhanced prompt is present
      if (!enhancedPrompts.prompt) {
        const errorMsg = 'Prompt enhancer did not return an enhanced prompt. Cannot proceed with video generation.';
        console.error('❌ [useVideoGeneration]', errorMsg);
        setError(errorMsg);
        setStatus('failed');
        if (onError) {
          onError(errorMsg);
        }
        return;
      }

      // Notify stage change: starting generation
      onStageChange?.('starting_generation', 'Starting video generation...');

      // Step 2: Call video generation API with enhanced prompts
      const { startImageToVideoJob } = await import('../services/videoGeneration');
      const response = await startImageToVideoJob({
        asset_id: assetId,
        image_url: enhancedPrompts.image_url,
        prompt: enhancedPrompts.prompt,
        negative_prompt: enhancedPrompts.negative_prompt,
        email: userEmail,
        anon_id: anonId,
        model: 'wan/v2.6/image-to-video',
      });

      // Notify stage change: generating video
      onStageChange?.('generating_video', 'Generating your video...');

      // Start time-based progress simulation for video generation (3 minutes max)
      const videoGenStartTime = Date.now();
      const VIDEO_GEN_MAX_TIME = 3 * 60 * 1000; // 3 minutes
      
      // Clear any existing interval
      if (videoGenProgressIntervalRef.current) {
        clearInterval(videoGenProgressIntervalRef.current);
      }
      
      // Simulate progress over 3 minutes, updating every 1 second for smoother updates on mobile
      // This ensures progress continues even if backend polling fails
      videoGenProgressIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - videoGenStartTime;
        // Progress from 0% to 90% over 3 minutes (cap at 90% until backend confirms completion)
        const simulatedProgress = Math.min(90, (elapsed / VIDEO_GEN_MAX_TIME) * 90);
        
        // Always update progress to ensure UI continues to show progress on mobile
        onStageProgress?.('generating_video', simulatedProgress);
        setProgress(Math.round(simulatedProgress));
      }, 1000); // Update every 1 second for smoother progress on mobile

      // Check if we have operation_id (new system), job_id (RunPod system), or asset_id (legacy system)
      const operationId = (response as any).operation_id;
      const jobId = response.job_id; // RunPod job ID - required for status polling
      const responseAssetId = response.asset_id;
      
      // Store videoGenStartTime in a ref so it's accessible in legacy polling
      const videoGenStartTimeRef = { current: videoGenStartTime };

      if (operationId) {
        // Use new polling system with operation_id
        
        try {
          await apiClient.pollVideoStatus({
            operation_id: operationId,
            asset_id: assetId, // Include asset_id in query params for polling
            onProgress: (status) => {
              // Use time-based simulation for progress (already running above)
              // Backend status is 'processing', so we continue with simulated progress
              // Ensure progress continues to update even if backend doesn't provide progress
              const elapsed = Date.now() - videoGenStartTime;
              const simulatedProgress = Math.min(90, (elapsed / VIDEO_GEN_MAX_TIME) * 90);
              onStageProgress?.('generating_video', simulatedProgress);
              setProgress(Math.round(simulatedProgress));
            },
            onComplete: async (status) => {
              // Clear progress simulation interval
              if (videoGenProgressIntervalRef.current) {
                clearInterval(videoGenProgressIntervalRef.current);
                videoGenProgressIntervalRef.current = null;
              }
              
              const elapsedTime = Date.now() - startTimeRef.current!;
              if (status.url) {
                // Set progress to 100% when complete
                onStageProgress?.('generating_video', 100);
                
                // Notify stage change: storing video
                onStageChange?.('storing_video_s3', 'Storing video in S3...');
                // Simulate storage progress
                setTimeout(() => {
                  onStageChange?.('storing_video_db', 'Finalizing...');
                  setTimeout(() => {
                    onStageChange?.('completed', 'Video ready!');
                  }, 500);
                }, 500);

                // Determine the correct video URL based on user's subscription
                const appropriateUrl = await determineVideoUrl(status.url, assetId);
                setVideoUrl(appropriateUrl);
                setProgress(100);
                setStatus('completed');
                
                // Invalidate video assets cache so My Collection refreshes
                invalidateUserAssetsCache('video');
              } else {
                // Completed but no URL - DO NOT ERROR OUT, continue polling
                console.warn('⚠️ [useVideoGeneration] Video marked as completed but no URL found - will continue polling');
                // DO NOT set error or failed status - let it continue polling
                // The pollVideoStatus will keep calling onProgress until URL appears
              }
            },
            onError: (error) => {
              // Only fail on actual errors, not network issues
              // Network issues are handled by the polling system itself
              console.error('❌ [useVideoGeneration] Video generation error:', error);
              
              // Clear progress simulation interval on error
              if (videoGenProgressIntervalRef.current) {
                clearInterval(videoGenProgressIntervalRef.current);
                videoGenProgressIntervalRef.current = null;
              }
              
              // Check if this is an insufficient credits error (could be string or object)
              let creditError: any = null;
              if (typeof error === 'object' && error !== null && 'type' in error) {
                const errorObj = error as { type?: string; message?: string; current_credits?: number; required_credits?: number };
                if (errorObj.type === 'insufficient_credits') {
                  creditError = errorObj;
                }
              } else if (typeof error === 'string') {
                const lowerError = error.toLowerCase();
                if (lowerError.includes('insufficient credits') || 
                    lowerError.includes('not enough credits') ||
                    (lowerError.includes('need') && lowerError.includes('credits'))) {
                  creditError = {
                    type: 'insufficient_credits',
                    message: error,
                    current_credits: 0,
                    required_credits: 100,
                  };
                }
              }
              
              const errorStr = typeof error === 'string' ? error : '';
              // Only set error if it's a real failure (not a network timeout)
              if (!errorStr.includes('timeout') && !errorStr.includes('network')) {
                if (creditError) {
                  setError(creditError.message || errorStr);
                  setStatus('failed');
                  onError?.(creditError); // Pass credit error object
                } else {
                  setError(errorStr);
                  setStatus('failed');
                  onError?.(errorStr);
                }
              } else {
                // Network error - just log, polling will continue
                console.warn('⚠️ [useVideoGeneration] Network error during polling, but will continue');
              }
            },
            interval: POLLING.VIDEO_STATUS,
          });
        } catch (err) {
          console.error('❌ [useVideoGeneration] Polling failed:', err);
          
          // Only fail on actual errors, not network issues
          const errorMessage = err instanceof Error ? err.message : 'Video generation failed';
          if (!errorMessage.includes('timeout') && !errorMessage.includes('network')) {
            setError(errorMessage);
            setStatus('failed');
          } else {
            // Network error - just log, but don't fail
            console.warn('⚠️ [useVideoGeneration] Network error during polling setup, but will continue');
          }
        }
      } else if (jobId && responseAssetId) {
        // Store refs for visibility change handler
        jobIdRef.current = jobId;
        assetIdRef.current = responseAssetId;
        
        // Use RunPod job polling system with job_id and asset_id
        const EIGHT_MINUTES_MS = 8 * 60 * 1000;

        let consecutiveErrors = 0;
        const MAX_CONSECUTIVE_ERRORS = 5; // Allow 5 consecutive errors before showing warning
        
        const pollInterval = setInterval(async () => {
          // Skip polling if page is hidden/paused
          if (isPollingPausedRef.current) {
            console.log('⏸️ [useVideoGeneration] Polling paused (page hidden)');
            return;
          }
          
          try {
            const elapsedTime = Date.now() - startTimeRef.current!;
            // Pass both job_id and asset_id to pollJob
            const jobStatus = await pollJob(jobId, responseAssetId);
            
            // Reset error counter on successful poll
            consecutiveErrors = 0;

            // Use backend progress and status ONLY
            // Use time-based simulation for progress (already running via interval)
            // If backend provides progress, blend it with time-based simulation
            if (jobStatus.progress !== undefined && jobStatus.progress > 0) {
              setProgress(jobStatus.progress);
              // Blend backend progress with time-based simulation (use whichever is higher)
              const elapsed = Date.now() - videoGenStartTimeRef.current;
              const simulatedProgress = Math.min(90, (elapsed / VIDEO_GEN_MAX_TIME) * 90);
              const blendedProgress = Math.max(simulatedProgress, jobStatus.progress);
              onStageProgress?.('generating_video', blendedProgress);
            } else {
              // Backend didn't provide progress, use time-based simulation only
              const elapsed = Date.now() - videoGenStartTimeRef.current;
              const simulatedProgress = Math.min(90, (elapsed / VIDEO_GEN_MAX_TIME) * 90);
              onStageProgress?.('generating_video', simulatedProgress);
              setProgress(Math.round(simulatedProgress));
            }
            // Note: Time-based simulation is already running via videoGenProgressIntervalRef

            if (jobStatus.status === 'completed') {
              // Clear progress simulation interval
              if (videoGenProgressIntervalRef.current) {
                clearInterval(videoGenProgressIntervalRef.current);
                videoGenProgressIntervalRef.current = null;
              }
              
              const finalVideoUrl = jobStatus.videoUrl;
              if (finalVideoUrl) {
                clearInterval(pollInterval);
                pollIntervalRef.current = null;
                
                // Set progress to 100% when complete
                onStageProgress?.('generating_video', 100);
                
                // Notify stage changes for final steps
                onStageChange?.('storing_video_s3', 'Storing video in S3...');
                setTimeout(() => {
                  onStageChange?.('storing_video_db', 'Finalizing...');
                  setTimeout(() => {
                    onStageChange?.('completed', 'Video ready!');
                  }, 500);
                }, 500);

                // Determine the correct video URL based on user's subscription
                const appropriateUrl = await determineVideoUrl(finalVideoUrl, responseAssetId);
                setVideoUrl(appropriateUrl);
                setProgress(100);
                setStatus('completed');
              } else {
                // Backend says completed but no URL - continue polling
                console.warn(`⚠️ [useVideoGeneration] Backend says completed but no videoUrl - continuing to poll...`);
                // Keep progress at backend value, don't update
              }
            } else if (jobStatus.status === 'failed') {
              // Clear progress simulation interval on failure
              if (videoGenProgressIntervalRef.current) {
                clearInterval(videoGenProgressIntervalRef.current);
                videoGenProgressIntervalRef.current = null;
              }
              
              clearInterval(pollInterval);
              pollIntervalRef.current = null;
              const errorMsg = jobStatus.error || 'Video generation failed. Please try again.';
              
              // Check if error contains insufficient credits information
              // The error might be a string or an object with credit details
              let creditError: any = null;
              if (typeof jobStatus.error === 'object' && jobStatus.error !== null) {
                const errorObj = jobStatus.error as { type?: string; message?: string; current_credits?: number; required_credits?: number };
                // Error is an object - check for credit error type
                if ('type' in errorObj && errorObj.type === 'insufficient_credits') {
                  creditError = errorObj;
                }
              } else if (typeof errorMsg === 'string') {
                // Check error message for credit-related keywords
                const lowerError = errorMsg.toLowerCase();
                if (lowerError.includes('insufficient credits') || 
                    lowerError.includes('not enough credits') ||
                    lowerError.includes('need') && lowerError.includes('credits')) {
                  // Try to extract credit information from error message or create a generic credit error
                  creditError = {
                    type: 'insufficient_credits',
                    message: errorMsg,
                    current_credits: 0,
                    required_credits: 100,
                  };
                }
              }
              
              // If we detected a credit error, pass it as an object to onError
              if (creditError) {
                const errorMessage = typeof creditError.message === 'string' ? creditError.message : typeof errorMsg === 'string' ? errorMsg : 'Video generation failed';
                setError(errorMessage);
                setStatus('failed');
                onError?.(creditError); // Pass the full error object
                console.error('❌ [useVideoGeneration] Video generation failed (insufficient credits):', creditError);
              } else {
                const errorMessage = typeof errorMsg === 'string' ? errorMsg : 'Video generation failed. Please try again.';
                setError(errorMessage);
                setStatus('failed');
                onError?.(errorMessage);
                console.error('❌ [useVideoGeneration] Video generation failed:', errorMessage);
              }
            } else if (elapsedTime >= EIGHT_MINUTES_MS) {
              // DO NOT FAIL - just log and continue polling
              // The backend might be slow but will eventually complete
              if (jobStatus.status === 'processing' || jobStatus.status === 'pending') {
                console.warn(`⏱️ [useVideoGeneration] 8 minutes elapsed but still processing - continuing to poll (will not timeout)`);
                // Continue polling - DO NOT ERROR OUT
              }
            }
          } catch (err) {
            consecutiveErrors++;
            const errorMessage = err instanceof Error ? err.message : String(err);
            console.error(`❌ [useVideoGeneration] Poll error (${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, errorMessage);
            
            // Check if the error message indicates the pollJob function tried Supabase fallback
            // and might have found a completed status that we should process
            // The pollJob function will throw an error only if both backend AND Supabase fallback fail
            // So if we get here, both failed - but we should still continue polling in case it recovers
            
            // If we have too many consecutive errors, try Supabase fallback directly
            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
              console.warn(`⚠️ [useVideoGeneration] Multiple consecutive poll errors - checking Supabase directly`);
              
              try {
                // Check asset status directly from Supabase
                const { checkAssetFromSupabase } = await import('../services/videoGeneration');
                const supabaseStatus = await checkAssetFromSupabase(responseAssetId);
                
                if (supabaseStatus && supabaseStatus.status === 'completed' && supabaseStatus.videoUrl) {
                  console.log('✅ [useVideoGeneration] Found completed video in Supabase!');
                  clearInterval(pollInterval);
                  pollIntervalRef.current = null;
                  
                  if (videoGenProgressIntervalRef.current) {
                    clearInterval(videoGenProgressIntervalRef.current);
                    videoGenProgressIntervalRef.current = null;
                  }
                  
                  const appropriateUrl = await determineVideoUrl(supabaseStatus.videoUrl, responseAssetId);
                  setVideoUrl(appropriateUrl);
                  setProgress(100);
                  setStatus('completed');
                  console.log('✅ [useVideoGeneration] Video recovered from Supabase after poll errors');
                  return;
                }
              } catch (supabaseErr) {
                console.error('❌ [useVideoGeneration] Supabase fallback also failed:', supabaseErr);
              }
              
              // Reset counter to allow recovery
              consecutiveErrors = 0;
            }
            
            // DO NOT FAIL - just log and continue polling
            // Network issues are temporary, backend will eventually respond
            // The pollJob function's Supabase fallback should catch completed videos
            // Continue polling - DO NOT ERROR OUT
          }
        }, 30000); // Legacy: Poll every 30 seconds

        pollIntervalRef.current = pollInterval;
      } else {
        throw new Error('No operation_id or asset_id returned from backend');
      }
    } catch (err) {
      // Clear any progress intervals on error
      if (videoGenProgressIntervalRef.current) {
        clearInterval(videoGenProgressIntervalRef.current);
        videoGenProgressIntervalRef.current = null;
      }
      
      console.error('❌ [useVideoGeneration] Generation start error:', err);
      
      // Determine error type for tracking
      let errorType = 'unknown';
      let errorMessage = 'Unknown error';
      
      if (err instanceof Error) {
        errorMessage = err.message;
        
        if (err.message.includes('timed out') || err.message.includes('timeout')) {
          errorType = 'request_timeout';
        } else if (err.message.includes('Load failed') || err.message.includes('Network request failed')) {
          errorType = 'network_load_failed';
        } else if (err.name === 'AbortError') {
          errorType = 'request_aborted';
        } else if (err.message.includes('backgrounded')) {
          errorType = 'page_backgrounded';
        }
      }
      
      // Track video generation failure with detailed context
      track('video_generation_failed', {
        error_type: errorType,
        error_message: errorMessage,
        stage: 'video_generation_start',
        asset_id: assetId,
        has_email: !!userEmail,
        user_agent: navigator.userAgent,
        is_mobile: /Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
        is_android: /Android/i.test(navigator.userAgent),
        is_ios: /iPhone|iPad|iPod/i.test(navigator.userAgent),
        is_facebook_browser: /FBAN|FBAV/i.test(navigator.userAgent),
        network_type: (navigator as any).connection?.effectiveType || 'unknown',
      });
      
      // Check if this is an insufficient credits error
      if (err && typeof err === 'object' && 'type' in err && err.type === 'insufficient_credits') {
        // Pass the error object to onError so components can handle it
        const creditError = err as any;
        setError(creditError.message || 'Insufficient credits');
        setStatus('failed');
        onError?.(creditError); // Pass the full error object, not just the message
        // Re-throw as-is so components can also catch it in try-catch
        throw err;
      }
      
      setError(err instanceof Error ? err.message : 'Failed to start generation');
      setStatus('failed');
      onError?.(err instanceof Error ? err.message : 'Failed to start generation');
    }
  }, [assetId, prompt, userEmail, onStageChange, onStageProgress, onError, determineVideoUrl]);

  const cancelGeneration = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (videoGenProgressIntervalRef.current) {
      clearInterval(videoGenProgressIntervalRef.current);
      videoGenProgressIntervalRef.current = null;
    }
    if (cancelSimulationRef.current) {
      cancelSimulationRef.current();
      cancelSimulationRef.current = null;
    }
    setStatus('idle');
  }, []);

  const reset = useCallback(() => {
    cancelGeneration();
    setStatus('idle');
    setProgress(0);
    setVideoUrl(null);
    setError(null);
    startTimeRef.current = null;
  }, [cancelGeneration]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelGeneration();
    };
  }, [cancelGeneration]);

  // Add page visibility detection to pause/resume polling
  useEffect(() => {
    const handleVisibilityChange = async () => {
      const isHidden = document.hidden;
      const now = Date.now();
      const timeSinceLastChange = now - lastVisibilityChangeRef.current;
      lastVisibilityChangeRef.current = now;
      
      if (status !== 'generating') {
        return; // Only handle visibility during active generation
      }
      
      if (isHidden) {
        // Page became hidden - pause polling
        console.log('👁️ [useVideoGeneration] Page hidden, pausing polling');
        isPollingPausedRef.current = true;
        
        track('video_generation_page_hidden', {
          asset_id: assetId,
          job_id: jobIdRef.current,
          time_generating_ms: Date.now() - (startTimeRef.current || 0),
          current_progress: progress,
        });
      } else {
        // Page became visible - resume polling and check status
        console.log('👁️ [useVideoGeneration] Page visible, resuming polling');
        isPollingPausedRef.current = false;
        
        track('video_generation_page_visible', {
          asset_id: assetId,
          job_id: jobIdRef.current,
          time_hidden_ms: timeSinceLastChange,
          current_progress: progress,
        });
        
        // Immediately check asset status from Supabase when page becomes visible
        // This helps recover if video completed while page was hidden
        if (assetIdRef.current) {
          try {
            console.log('🔍 [useVideoGeneration] Checking Supabase for updates after visibility change');
            const { checkAssetFromSupabase } = await import('../services/videoGeneration');
            const supabaseStatus = await checkAssetFromSupabase(assetIdRef.current);
            
            if (supabaseStatus) {
              if (supabaseStatus.status === 'completed' && supabaseStatus.videoUrl) {
                console.log('✅ [useVideoGeneration] Video completed while page was hidden!');
                
                // Clear all intervals
                if (pollIntervalRef.current) {
                  clearInterval(pollIntervalRef.current);
                  pollIntervalRef.current = null;
                }
                if (videoGenProgressIntervalRef.current) {
                  clearInterval(videoGenProgressIntervalRef.current);
                  videoGenProgressIntervalRef.current = null;
                }
                
                // Update state
                const appropriateUrl = await determineVideoUrl(supabaseStatus.videoUrl, assetIdRef.current);
                setVideoUrl(appropriateUrl);
                setProgress(100);
                setStatus('completed');
                
                track('video_generation_recovered_after_hidden', {
                  asset_id: assetIdRef.current,
                  time_hidden_ms: timeSinceLastChange,
                });
              } else if (supabaseStatus.status === 'failed') {
                console.error('❌ [useVideoGeneration] Video failed while page was hidden');
                const errorMsg = supabaseStatus.error || 'Video generation failed';
                
                // Check if error contains insufficient credits information
                let creditError: any = null;
                if (typeof errorMsg === 'object' && errorMsg !== null) {
                  const errorObj = errorMsg as { type?: string; message?: string; current_credits?: number; required_credits?: number };
                  if ('type' in errorObj && errorObj.type === 'insufficient_credits') {
                    creditError = errorObj;
                  }
                } else if (typeof errorMsg === 'string') {
                  const lowerError = errorMsg.toLowerCase();
                  if (lowerError.includes('insufficient credits') || 
                      lowerError.includes('not enough credits') ||
                      (lowerError.includes('need') && lowerError.includes('credits'))) {
                    creditError = {
                      type: 'insufficient_credits',
                      message: errorMsg,
                      current_credits: 0,
                      required_credits: 100,
                    };
                  }
                }
                
                if (creditError) {
                  const errorMessage = typeof creditError.message === 'string' ? creditError.message : typeof errorMsg === 'string' ? errorMsg : 'Video generation failed';
                  setError(errorMessage);
                  setStatus('failed');
                  onError?.(creditError);
                } else {
                  const errorMessage = typeof errorMsg === 'string' ? errorMsg : 'Video generation failed';
                  setError(errorMessage);
                  setStatus('failed');
                  onError?.(errorMessage);
                }
              } else {
                console.log('⏳ [useVideoGeneration] Video still processing, continuing to poll');
              }
            }
          } catch (err) {
            console.error('❌ [useVideoGeneration] Failed to check Supabase on visibility change:', err);
            // Continue with normal polling
          }
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [status, assetId, progress, track, determineVideoUrl]);

  return {
    status,
    progress,
    videoUrl,
    error,
    startGeneration,
    cancelGeneration,
    reset,
  };
}
